import { assets } from './assets';
import type {
  Action,
  ActionWithId,
  EventEntry,
  GameObjectWithId,
  GameType,
  Inventory,
  ItemType,
  ItemWithId,
  Map,
  Modifier,
  PlayerStance,
  Position,
  PublicPlayer,
  TemporaryStatus,
} from './backend';
import { enumIs } from './enums';
import { renderMap } from './map';
import { Messages } from './messages';
import {
  processItemEvents,
  processGameEvents,
  updateObjectAt,
  updatePlayerAt,
} from './objects';
import type { Particle } from './particles';
import { unreachable } from './unreachable';

const SCROLL_SPEED = 20;

export type Location = {
  x: number;
  y: number;
  angle: number;
};

export class Cursor {
  constructor(public x: number, public y: number) {}

  public update(deltaX: number, deltaY: number, maxW: number, maxH: number) {
    this.x += deltaX;
    this.y += deltaY;

    if (this.x < 0) {
      this.x = 0;
    } else if (this.x > maxW) {
      this.x = maxW;
    }

    if (this.y < 0) {
      this.y = 0;
    } else if (this.y > maxH) {
      this.y = maxH;
    }
  }

  public draw(
    context: CanvasRenderingContext2D,
    _renderer: Renderer,
    color = 'white'
  ) {
    context.strokeStyle = color;

    context.beginPath();
    context.moveTo(this.x - 5, this.y - 5);
    context.lineTo(this.x + 5, this.y + 5);
    context.moveTo(this.x + 5, this.y - 5);
    context.lineTo(this.x - 5, this.y + 5);

    // 2 pixel wide line
    context.lineWidth = 2;

    context.stroke();
  }
}

export type MapBrush = {
  image: HTMLCanvasElement;
  x: number;
  y: number;
  w: number;
  h: number;
  id: number;
};

export type MapHouse = {
  image: HTMLCanvasElement;
  x: number;
  y: number;
  w: number;
  h: number;
  id: number;
};

export class Renderer {
  cursor: Cursor;
  mapOffset: { x: number; y: number };
  renderedMap: HTMLCanvasElement;
  brushes: MapBrush[];
  houses: MapHouse[];

  constructor(
    public map: Map,
    public tileSize: number // public canvas: HTMLCanvasElement
  ) {
    const { ground, brushes, houses } = renderMap(map);
    this.brushes = brushes;
    this.houses = houses;
    this.renderedMap = ground;
    this.cursor = new Cursor(0, 0);
    this.mapOffset = { x: 0, y: 0 };
  }

  public checkEdgeScroll(canvas: HTMLCanvasElement) {
    if (this.cursor.x == 0) {
      this.mapOffset.x -= SCROLL_SPEED;
    }
    if (this.cursor.x == canvas.width) {
      this.mapOffset.x += SCROLL_SPEED;
    }

    if (this.cursor.y == 0) {
      this.mapOffset.y -= SCROLL_SPEED;
    }

    if (this.cursor.y == canvas.height) {
      this.mapOffset.y += SCROLL_SPEED;
    }

    if (this.renderedMap.width < canvas.width) {
      // center it horizontally
      this.mapOffset.x = (this.renderedMap.width - canvas.width) / 2;
    } else {
      this.mapOffset.x = Math.min(
        Math.max(-300, this.mapOffset.x),
        this.renderedMap.width - canvas.width + 300
      );
    }

    if (this.renderedMap.height < canvas.height) {
      // center it vertically
      this.mapOffset.y = (this.renderedMap.height - canvas.height) / 2;
    } else {
      this.mapOffset.y = Math.min(
        Math.max(-300, this.mapOffset.y),
        this.renderedMap.height - canvas.height + 300
      );
    }
  }

  public toCanvasCoords(x: number, y: number): { x: number; y: number } {
    return {
      x: x * this.tileSize - this.mapOffset.x,
      y: y * this.tileSize - this.mapOffset.y,
    };
  }

  public canvasToWorldSpace(x: number, y: number) {
    const worldX = (x + this.mapOffset.x) / this.tileSize;
    const worldY = (y + this.mapOffset.y) / this.tileSize;

    return { x: worldX, y: worldY };
  }

  public toCanvasCoordsAsArgs(x: number, y: number): [number, number] {
    return [
      x * this.tileSize - this.mapOffset.x,
      y * this.tileSize - this.mapOffset.y,
    ];
  }

  public setViewByGameCoords(x: number, y: number, canvas: HTMLCanvasElement) {
    this.mapOffset = {
      x: Math.min(
        Math.max(0, x * this.tileSize - canvas.width / 2),
        this.renderedMap.width - canvas.width
      ),
      y: Math.min(
        Math.max(0, y * this.tileSize - canvas.height / 2),
        this.renderedMap.height - canvas.height
      ),
    };
  }

  public drawBrushes(context: CanvasRenderingContext2D, player: Location) {
    const canvas = context.canvas;

    const playerTile =
      this.map.tiles[Math.floor(player.y)]?.[Math.floor(player.x)];
    let playerBrushId: number | null = null;
    if (playerTile && enumIs(playerTile, 'Brush')) {
      playerBrushId = playerTile.Brush.id;
    }

    const cursorToWorld = this.canvasToWorldSpace(this.cursor.x, this.cursor.y);

    const cursorTile =
      this.map.tiles[Math.floor(cursorToWorld.y)]?.[
        Math.floor(cursorToWorld.x)
      ];
    let cursorBrushId: number | null = null;
    if (cursorTile && enumIs(cursorTile, 'Brush')) {
      cursorBrushId = cursorTile.Brush.id;
    }

    for (const brush of this.brushes) {
      if (
        brush.x * this.tileSize + brush.w * this.tileSize < this.mapOffset.x ||
        brush.y * this.tileSize + brush.h * this.tileSize < this.mapOffset.y ||
        brush.x * this.tileSize > this.mapOffset.x + canvas.width ||
        brush.y * this.tileSize > this.mapOffset.y + canvas.height
      ) {
        continue;
      }

      if (playerBrushId === brush.id) {
        context.globalAlpha = 0.5;
      } else if (cursorBrushId === brush.id) {
        context.globalAlpha = 0.75;
      } else {
        context.globalAlpha = 1;
      }

      context.drawImage(
        brush.image,
        brush.x * this.tileSize - this.mapOffset.x,
        brush.y * this.tileSize - this.mapOffset.y,
        brush.w * this.tileSize,
        brush.h * this.tileSize
      );
    }
    context.globalAlpha = 1;
  }

  public drawHouses(context: CanvasRenderingContext2D, player: Location) {
    const canvas = context.canvas;

    const playerTile =
      this.map.tiles[Math.floor(player.y)]?.[Math.floor(player.x)];

    let playerHouseId: number | null = null;
    if (playerTile && enumIs(playerTile, 'Indoors')) {
      playerHouseId = playerTile.Indoors.id;
    }
    if (playerTile && enumIs(playerTile, 'HouseDoor')) {
      playerHouseId = playerTile.HouseDoor.id;
    }

    for (const house of this.houses) {
      if (
        house.x * this.tileSize + house.w * this.tileSize < this.mapOffset.x ||
        house.y * this.tileSize + house.h * this.tileSize < this.mapOffset.y ||
        house.x * this.tileSize > this.mapOffset.x + canvas.width ||
        house.y * this.tileSize > this.mapOffset.y + canvas.height
      ) {
        continue;
      }

      if (playerHouseId === house.id) {
        context.globalAlpha = 0;
      } else {
        context.globalAlpha = 0.5;
      }

      context.drawImage(
        house.image,
        house.x * this.tileSize - this.mapOffset.x,
        house.y * this.tileSize - this.mapOffset.y,
        house.w * this.tileSize,
        house.h * this.tileSize
      );
    }
    context.globalAlpha = 1;
  }

  public drawMap(context: CanvasRenderingContext2D) {
    // const t0 = performance.now();

    const canvas = context.canvas;
    context.clearRect(0, 0, canvas.width, canvas.height);

    const sx = Math.max(0, this.mapOffset.x);
    const sy = Math.max(0, this.mapOffset.y);
    const sw = Math.min(canvas.width, this.renderedMap.width - sx);
    const sh = Math.min(canvas.height, this.renderedMap.height - sy);

    context.drawImage(
      this.renderedMap,
      sx,
      sy,
      sw,
      sh,
      this.mapOffset.x < 0 ? -this.mapOffset.x : 0,
      this.mapOffset.y < 0 ? -this.mapOffset.y : 0,
      sw,
      sh
    );

    // const t1 = performance.now();
    // console.log(`Took ${t1 - t0}ms to draw map`);
  }

  public drawCenterMarker(context: CanvasRenderingContext2D) {
    // draw assets.flag Image at center of this.map
    const center = this.toCanvasCoords(
      Number(this.map.width) / 2,
      Number(this.map.height) / 2
    );
    context.drawImage(
      assets.images.flag,
      center.x - this.tileSize / 2,
      center.y - this.tileSize / 2,
      this.tileSize,
      this.tileSize
    );
  }
}

export class PlayerActionQueue {
  lastQueue: ActionWithId[] = [];
  lastActionId = 0n;
  finalLoc: Position;
  newActions: ActionWithId[] = [];

  public updateWith(
    committedActions: ActionWithId[],
    lastProcessedActionId: bigint
  ) {
    this.lastQueue = committedActions;
    this.lastActionId = lastProcessedActionId;
    this.newActions = this.newActions.filter((a) => a.id > this.lastActionId);
  }

  public recordAction(
    action: Action,
    immediate: boolean = false
  ): ActionWithId {
    const actionWithId = {
      id: BigInt(Date.now()),
      action,
      immediate,
    };
    this.newActions.push(actionWithId);
    return actionWithId;
  }

  public getQueue(): ActionWithId[] {
    return [...this.lastQueue, ...this.newActions];
  }

  public getLastActionPosition(): Position {
    return this.finalLoc;
  }

  public draw(
    context: CanvasRenderingContext2D,
    renderer: Renderer,
    currentPosition: Location
  ) {
    const queue = this.getQueue();

    queue.sort((a, b) => Number(a.id - b.id));

    let pos = { x: currentPosition.x, y: currentPosition.y };
    context.strokeStyle = 'yellow';
    context.beginPath();
    context.moveTo(...renderer.toCanvasCoordsAsArgs(pos.x, pos.y));

    this.finalLoc = pos;

    for (const item of queue) {
      const { action } = item;
      const isNewAction = this.newActions.includes(item);

      if (enumIs(action, 'Move')) {
        pos = { x: action.Move.to.x, y: action.Move.to.y };
        context.beginPath();
        context.moveTo(
          ...renderer.toCanvasCoordsAsArgs(this.finalLoc.x, this.finalLoc.y)
        );
        if (isNewAction) {
          context.setLineDash([5, 5]);
        } else {
          context.setLineDash([]);
        }
        context.strokeStyle = 'yellow';
        context.lineTo(...renderer.toCanvasCoordsAsArgs(pos.x, pos.y));
        context.stroke();
        context.setLineDash([]);

        context.fillStyle = 'yellow';
        context.beginPath();
        context.arc(
          ...renderer.toCanvasCoordsAsArgs(pos.x, pos.y),
          5,
          0,
          2 * Math.PI
        );
        context.fill();

        this.finalLoc = pos;
      } else if (enumIs(action, 'Guard')) {
        pos = { x: action.Guard.to.x, y: action.Guard.to.y };
        context.beginPath();
        context.moveTo(
          ...renderer.toCanvasCoordsAsArgs(this.finalLoc.x, this.finalLoc.y)
        );
        if (isNewAction) {
          context.setLineDash([5, 5]);
        } else {
          context.setLineDash([]);
        }
        context.strokeStyle = 'red';
        context.lineTo(...renderer.toCanvasCoordsAsArgs(pos.x, pos.y));
        context.stroke();
        context.setLineDash([]);

        context.fillStyle = 'red';
        context.beginPath();
        context.arc(
          ...renderer.toCanvasCoordsAsArgs(pos.x, pos.y),
          5,
          0,
          2 * Math.PI
        );
        context.fill();

        this.finalLoc = pos;
      } else if (enumIs(action, 'DropWeapon')) {
        context.strokeStyle = 'yellow';
        context.lineTo(...renderer.toCanvasCoordsAsArgs(pos.x, pos.y));
        context.stroke();
        // draw 2 concentric red-stroke circles
        context.strokeStyle = 'blue';
        context.beginPath();
        context.arc(
          ...renderer.toCanvasCoordsAsArgs(pos.x, pos.y),
          renderer.tileSize / 2,
          0,
          2 * Math.PI
        );
        context.stroke();
      } else if (enumIs(action, 'Stop')) {
      } else if (enumIs(action, 'ChangeStanceTo')) {
      } else if (enumIs(action, 'Leave')) {
      } else if (enumIs(action, 'UseItem')) {
      } else if (enumIs(action, 'DropItemFromInventory')) {
      } else if (enumIs(action, 'UseItemAt')) {
        // draw a red line to the target and draw the item at the target on a white filled circle
        context.strokeStyle = 'red';
        if (isNewAction) {
          context.setLineDash([5, 5]);
        }
        context.lineTo(
          ...renderer.toCanvasCoordsAsArgs(
            action.UseItemAt.at.x,
            action.UseItemAt.at.y
          )
        );

        context.stroke();
        context.closePath();

        context.setLineDash([]);

        context.fillStyle = 'white';
        context.beginPath();
        context.arc(
          ...renderer.toCanvasCoordsAsArgs(
            action.UseItemAt.at.x,
            action.UseItemAt.at.y
          ),
          renderer.tileSize / 2,
          0,
          2 * Math.PI
        );
        context.fill();
        context.closePath();
        context.beginPath();
        context.moveTo(...renderer.toCanvasCoordsAsArgs(pos.x, pos.y));
      } else {
        unreachable(action);
      }
    }

    context.closePath();
  }
}

export class EnemyPlayer {
  public state: PlayerState = 'idle';
  public weapon: ItemWithId | null = null;
  public moveSpeed = 1.0;
  public armor = 0;
  public attackSpeed = 1.0;
  public stance: PlayerStance;
  public temporaryStatuses: TemporaryStatus[];
  public inventory: Inventory | null;

  constructor(
    public name: string,
    public principalText: string,
    public loc: Location | null,
    public health: number | null,
    public maxHealth: number | null
  ) {}

  public draw(context: CanvasRenderingContext2D, renderer: Renderer) {
    if (!this.loc) {
      return;
    }

    const canvasLoc = renderer.toCanvasCoordsAsArgs(this.loc.x, this.loc.y);

    if (this.health == 0) {
      // draw image of tombstone from `assets.dead`
      context.drawImage(
        assets.images.dead,
        ...canvasLoc,
        renderer.tileSize,
        renderer.tileSize
      );
    } else {
      context.fillStyle = 'red';
      context.beginPath();
      context.arc(...canvasLoc, renderer.tileSize / 2, 0, 2 * Math.PI);
      context.fill();

      context.strokeStyle = 'rgba(200, 200, 0, 0.5)';
      context.beginPath();
      context.moveTo(...canvasLoc);
      const dest = renderer.toCanvasCoords(this.loc.x, this.loc.y);
      const lineLength = this.weapon
        ? renderer.tileSize
        : renderer.tileSize / 2;

      const angleDest = [
        dest.x + Math.cos(this.loc.angle) * lineLength,
        dest.y + Math.sin(this.loc.angle) * lineLength,
      ];
      context.lineTo(angleDest[0], angleDest[1]);
      context.stroke();

      // context.translate
      if (this.weapon) {
        if (enumIs(this.weapon.item_type, 'Knife')) {
          // draw knife at angleDest in the direction of this.loc.angle
          context.save();
          context.translate(angleDest[0], angleDest[1]);

          context.rotate(this.loc.angle);
          if (this.loc.angle < -Math.PI / 2 || this.loc.angle > Math.PI / 2) {
            context.scale(1, -1);
          }

          context.drawImage(
            assets.images.items.knife,
            -renderer.tileSize / 2,
            -renderer.tileSize / 2,
            renderer.tileSize,
            renderer.tileSize
          );

          context.restore();
        } else if (enumIs(this.weapon.item_type, 'Pistol')) {
          context.save();
          context.translate(angleDest[0], angleDest[1]);

          context.rotate(this.loc.angle + Math.PI);
          if (this.loc.angle < -Math.PI / 2 || this.loc.angle > Math.PI / 2) {
          } else {
            context.scale(1, -1);
          }

          context.drawImage(
            assets.images.items.pistol,
            -renderer.tileSize / 2,
            -renderer.tileSize / 2,
            renderer.tileSize,
            renderer.tileSize
          );

          context.restore();
        }
      }

      const healthBarWidth = Math.floor(this.maxHealth * 0.6);
      // draw health bar above
      context.fillStyle = 'black';
      context.fillRect(
        canvasLoc[0] - healthBarWidth / 2,
        canvasLoc[1] - 30,
        healthBarWidth,
        10
      );
      context.fillStyle = 'red';
      context.fillRect(
        canvasLoc[0] - healthBarWidth / 2 + 1,
        canvasLoc[1] - 30,
        (healthBarWidth - 2) * (this.health / this.maxHealth),
        8
      );

      // write name above health bar
      context.fillStyle = 'white';
      context.font = '12px sans-serif';
      context.textAlign = 'center';
      context.fillText(this.name, canvasLoc[0], canvasLoc[1] - 45);
    }
  }
}

// export abstract class AnimatedObjectState {
//   // public abstract update(gameBlockTime: number, events: EventEntry[]);
//   public abstract draw(context: CanvasRenderingContext2D, renderer: Renderer);
// }

export class ClientOwnPlayer {
  public queue: PlayerActionQueue = new PlayerActionQueue();
  public state: PlayerState = 'idle';
  public weapon: ItemWithId | null = null;
  public moveSpeed = 1.0;
  public attackSpeed = 1.0;
  public armor = 0;
  public damage = 0;
  public inventory: Inventory | null = null;
  public stance: PlayerStance = { Defensive: null };
  public temporaryStatuses: TemporaryStatus[];

  constructor(
    public principalText: string,
    public loc: Location,
    public health: number,
    public maxHealth: number
  ) {}

  public draw(context: CanvasRenderingContext2D, renderer: Renderer) {
    if (this.health == 0) {
      // draw image of tombstone from `assets.dead`
      context.drawImage(
        assets.images.dead,
        ...renderer.toCanvasCoordsAsArgs(this.loc.x, this.loc.y),
        renderer.tileSize,
        renderer.tileSize
      );
    } else {
      const canvasLoc = renderer.toCanvasCoordsAsArgs(this.loc.x, this.loc.y);

      context.fillStyle = 'blue';
      context.beginPath();
      context.arc(...canvasLoc, renderer.tileSize / 2, 0, 2 * Math.PI);
      context.fill();

      context.strokeStyle = 'rgba(200, 200, 0, 0.5)';
      context.beginPath();
      context.moveTo(...canvasLoc);
      const dest = renderer.toCanvasCoords(this.loc.x, this.loc.y);

      const lineLength = this.weapon
        ? renderer.tileSize
        : renderer.tileSize / 2;

      const angleDest = [
        dest.x + Math.cos(this.loc.angle) * lineLength,
        dest.y + Math.sin(this.loc.angle) * lineLength,
      ];
      context.lineTo(angleDest[0], angleDest[1]);
      context.stroke();

      // context.translate
      if (this.weapon) {
        if (enumIs(this.weapon.item_type, 'Knife')) {
          // draw knife at angleDest in the direction of this.loc.angle
          context.save();
          context.translate(angleDest[0], angleDest[1]);

          context.rotate(this.loc.angle);
          if (this.loc.angle < -Math.PI / 2 || this.loc.angle > Math.PI / 2) {
            context.scale(1, -1);
          }

          context.drawImage(
            assets.images.items.knife,
            -renderer.tileSize / 2,
            -renderer.tileSize / 2,
            renderer.tileSize,
            renderer.tileSize
          );

          context.restore();
        } else if (enumIs(this.weapon.item_type, 'Pistol')) {
          context.save();
          context.translate(angleDest[0], angleDest[1]);

          context.rotate(this.loc.angle + Math.PI);
          if (this.loc.angle < -Math.PI / 2 || this.loc.angle > Math.PI / 2) {
          } else {
            context.scale(1, -1);
          }

          context.drawImage(
            assets.images.items.pistol,
            -renderer.tileSize / 2,
            -renderer.tileSize / 2,
            renderer.tileSize,
            renderer.tileSize
          );

          context.restore();
        }
      }

      const healthBarWidth = Math.floor(this.maxHealth * 0.6);
      // draw health bar above
      context.fillStyle = 'black';
      context.fillRect(
        canvasLoc[0] - healthBarWidth / 2,
        canvasLoc[1] - 30,
        healthBarWidth,
        10
      );
      context.fillStyle = 'chartreuse';
      context.fillRect(
        canvasLoc[0] - healthBarWidth / 2 + 1,
        canvasLoc[1] - 30,
        (healthBarWidth - 2) * (this.health / this.maxHealth),
        8
      );

      // console.log(`health: ${this.health}/${this.maxHealth}`);

      switch (this.state) {
        case 'idle':
          break;
        case 'guarding':
          // draw Image in assets.guarding above the player
          context.drawImage(
            assets.images.guarding,
            canvasLoc[0] - renderer.tileSize / 2,
            canvasLoc[1] - renderer.tileSize / 2 - 10,
            renderer.tileSize,
            renderer.tileSize
          );
          break;
        case 'walking':
          break;
      }
    }
  }
}

export type HudRegion =
  | {
      type: 'inventorySlot';
      slot: number;
      item: ItemWithId | null;
      x: number;
      y: number;
      w: number;
      h: number;
    }
  | {
      type: 'weaponSlot';
      item: ItemWithId | null;
      x: number;
      y: number;
      w: number;
      h: number;
    }
  | {
      type: 'stance';
      stance: PlayerStance;
      x: number;
      y: number;
      w: number;
      h: number;
    };

export type PlayerState = 'idle' | 'guarding' | 'walking';

export class ClientGame {
  particles: Particle[] = [];
  visibleItems: ItemWithId[] = [];
  gameObjects: GameObjectWithId[] = [];
  messages: Messages = new Messages(5000);

  constructor(
    public playerPublicInfo: PublicPlayer[],
    public ownPlayer: ClientOwnPlayer,
    public otherPlayers: EnemyPlayer[],
    public gameStartedAt: number,
    public gameType: GameType
  ) {}

  // public processEvents(events: EventEntry[]) {
  //   for (const event of events) {
  //     const principalText = event.player.toText();
  //     if (principalText == this.ownPlayer.principalText) {
  //       // this.ownPlayer.
  //     }
  //   }
  // }

  public beginBlock() {}

  public updateObjects(gameBlockTime: number, events: EventEntry[]) {
    this.particles = this.particles.filter((p) => !p.done);

    const ownEvents = events.filter(
      (e) =>
        enumIs(e.data, 'Player') &&
        e.data.Player.player.toText() == this.ownPlayer.principalText
    );

    this.particles.push(
      ...updatePlayerAt(this.ownPlayer, ownEvents, gameBlockTime)
    );

    for (const enemy of this.otherPlayers) {
      if (enemy.principalText == this.ownPlayer.principalText) {
        continue;
      }
      const enemyEvents = events.filter(
        (e) =>
          enumIs(e.data, 'Player') &&
          e.data.Player.player.toText() == enemy.principalText
      );

      if (enemyEvents.length == 0) {
        enemy.loc = null;
      }

      this.particles.push(...updatePlayerAt(enemy, enemyEvents, gameBlockTime));
    }

    // reset all object locations
    for (const obj of this.gameObjects) {
      if (enumIs(obj.object_type, 'Grenade')) {
        obj.object_type.Grenade.loc = null;
      } else {
        unreachable(obj.object_type);
      }
    }

    // register new objects
    for (const event of events) {
      if (!enumIs(event.data, 'Object')) continue;
      const data = event.data.Object;
      if (!this.gameObjects.find((o) => o.id == data.object.id)) {
        this.gameObjects.push(data.object);
      }
    }

    // update object states
    for (const obj of this.gameObjects) {
      const objEvents = events.filter(
        (e) => enumIs(e.data, 'Object') && e.data.Object.object.id == obj.id
      );

      this.particles.push(...updateObjectAt(obj, objEvents, gameBlockTime));
    }

    processGameEvents(events, this.playerPublicInfo, this.messages);

    this.visibleItems = processItemEvents(events, this.messages);
  }

  deathZoneCanvas: HTMLCanvasElement = document.createElement('canvas');

  public drawDeathZone(context: CanvasRenderingContext2D, renderer: Renderer) {
    if (
      this.deathZoneCanvas.width != context.canvas.width ||
      this.deathZoneCanvas.height != context.canvas.height
    ) {
      this.deathZoneCanvas.width = context.canvas.width;
      this.deathZoneCanvas.height = context.canvas.height;
    }

    const deathZoneContext = this.deathZoneCanvas.getContext('2d')!;

    let gameLengthSec = (this.otherPlayers.length + 1) * 120;

    let gameTimeSec = (Date.now() - this.gameStartedAt) / 1000;

    let zoneProgress = gameTimeSec / gameLengthSec;
    let deathZoneRadius = Math.max(
      0,
      ((1.0 - zoneProgress) * Math.SQRT2 * Number(renderer.map.width)) / 2.0
    );

    // console.log(gameTimeSec, gameLengthSec, zoneProgress, deathZoneRadius);

    deathZoneContext.clearRect(
      0,
      0,
      deathZoneContext.canvas.width,
      deathZoneContext.canvas.height
    );
    deathZoneContext.globalAlpha = 0.4;
    deathZoneContext.fillStyle = 'red';

    deathZoneContext.fillRect(
      0,
      0,
      deathZoneContext.canvas.width,
      deathZoneContext.canvas.height
    );

    // console.log(deathZoneContext.canvas.width, deathZoneContext.canvas.height);

    deathZoneContext.globalCompositeOperation = 'destination-out';
    deathZoneContext.globalAlpha = 1.0;

    // deathZoneContext.fillStyle = 'black';

    deathZoneContext.beginPath();
    deathZoneContext.arc(
      ...renderer.toCanvasCoordsAsArgs(
        Number(renderer.map.width) / 2,
        Number(renderer.map.height) / 2
      ),
      renderer.tileSize * deathZoneRadius,
      0,
      2 * Math.PI
    );
    deathZoneContext.fill();

    deathZoneContext.globalCompositeOperation = 'source-over';
    context.drawImage(this.deathZoneCanvas, 0, 0);
  }

  fogOfWarCanvas: HTMLCanvasElement = document.createElement('canvas');

  public drawFogOfWar(context: CanvasRenderingContext2D, renderer: Renderer) {
    if (
      this.fogOfWarCanvas.width != context.canvas.width ||
      this.fogOfWarCanvas.height != context.canvas.height
    ) {
      this.fogOfWarCanvas.width = context.canvas.width;
      this.fogOfWarCanvas.height = context.canvas.height;
    }

    const fogContext = this.fogOfWarCanvas.getContext('2d')!;
    fogContext.clearRect(
      0,
      0,
      fogContext.canvas.width,
      fogContext.canvas.height
    );
    fogContext.globalAlpha = 0.6;
    fogContext.fillStyle = 'black';

    fogContext.fillRect(
      0,
      0,
      fogContext.canvas.width,
      fogContext.canvas.height
    );

    fogContext.globalCompositeOperation = 'destination-out';
    fogContext.fillStyle = 'black';

    const playerTile =
      renderer.map.tiles[Math.floor(this.ownPlayer.loc.y)][
        Math.floor(this.ownPlayer.loc.x)
      ];

    const maybeHouseId = enumIs(playerTile, 'HouseDoor')
      ? playerTile.HouseDoor.id
      : enumIs(playerTile, 'Indoors')
      ? playerTile.Indoors.id
      : null;

    const drawFogOfWar = !enumIs(playerTile, 'Indoors');

    if (maybeHouseId !== null) {
      const house = renderer.houses.find((h) => h.id == maybeHouseId);
      fogContext.drawImage(
        house.image,
        house.x * renderer.tileSize - renderer.mapOffset.x,
        house.y * renderer.tileSize - renderer.mapOffset.y,
        house.w * renderer.tileSize,
        house.h * renderer.tileSize
      );
    }
    if (drawFogOfWar) {
      fogContext.beginPath();
      fogContext.arc(
        ...renderer.toCanvasCoordsAsArgs(
          this.ownPlayer.loc.x,
          this.ownPlayer.loc.y
        ),
        renderer.tileSize * 20,
        0,
        2 * Math.PI
      );
      fogContext.fill();
    }
    fogContext.globalCompositeOperation = 'source-over';

    // // cut out filled circle at player position with diameter of 30*tile_size

    // draw fog of war
    context.drawImage(this.fogOfWarCanvas, 0, 0);
  }

  public hasPlayerWon() {
    return (
      this.ownPlayer.health > 0 &&
      this.playerPublicInfo.every(
        (p) => p.dead || p.principal.toText() == this.ownPlayer.principalText
      )
    );
  }

  static itemHasModifier(item: ItemWithId | null) {
    return (
      item &&
      ((enumIs(item.item_type, 'Pistol') &&
        item.item_type.Pistol.modifiers.length > 0) ||
        (enumIs(item.item_type, 'Knife') &&
          item.item_type.Knife.modifiers.length > 0) ||
        (enumIs(item.item_type, 'Armor') &&
          item.item_type.Armor.modifiers.length > 0) ||
        (enumIs(item.item_type, 'Boots') &&
          item.item_type.Boots.modifiers.length > 0))
    );
  }

  static getModifierDamage(modifiers: Modifier[]): number {
    let damage = 0;
    for (const mod of modifiers) {
      if (enumIs(mod, 'ExtraDamage')) {
        damage += mod.ExtraDamage;
      }
    }
    return damage;
  }

  public drawHud(
    context: CanvasRenderingContext2D,
    renderer: Renderer
  ): HudRegion[] {
    const hudWidth = 380,
      hudHeight = 100;

    const hudRegions: HudRegion[] = [];

    // draw rect at bottom center
    context.fillStyle = 'rgba(100, 116, 139, 0.8)';
    context.fillRect(
      context.canvas.width / 2 - hudWidth / 2,
      context.canvas.height - hudHeight,
      hudWidth,
      hudHeight
    );

    if (ClientGame.itemHasModifier(this.ownPlayer.weapon)) {
      context.fillStyle = '#0000FF80';
    } else {
      context.fillStyle = '#00000080';
    }

    context.beginPath();
    context.arc(
      context.canvas.width / 2 - hudWidth / 2 + 40,
      context.canvas.height - hudHeight + 40,
      30,
      0,
      2 * Math.PI
    );
    context.fill();

    context.fillStyle = 'white';
    context.font = '11px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    let damage = 0;
    let range = 0;

    const weaponRegion = {
      x: context.canvas.width / 2 - hudWidth / 2 + 20,
      y: context.canvas.height - hudHeight + 20,
      w: 40,
      h: 40,
    };

    if (this.ownPlayer.weapon) {
      hudRegions.push({
        type: 'weaponSlot',
        item: this.ownPlayer.weapon,
        ...weaponRegion,
      });
      if (enumIs(this.ownPlayer.weapon.item_type, 'Pistol')) {
        context.drawImage(
          assets.images.items.pistol,
          context.canvas.width / 2 - hudWidth / 2 + 20,
          context.canvas.height - hudHeight + 20,
          40,
          40
        );
        context.fillText(
          'Pistol',
          context.canvas.width / 2 - hudWidth / 2 + 40,
          context.canvas.height - hudHeight + 85
        );
        damage =
          this.ownPlayer.weapon.item_type.Pistol.damage +
          ClientGame.getModifierDamage(
            this.ownPlayer.weapon.item_type.Pistol.modifiers
          );
        range = this.ownPlayer.weapon.item_type.Pistol.range;
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'Knife')) {
        context.drawImage(
          assets.images.items.knife,
          context.canvas.width / 2 - hudWidth / 2 + 20,
          context.canvas.height - hudHeight + 20,
          40,
          40
        );
        context.fillText(
          'Knife',
          context.canvas.width / 2 - hudWidth / 2 + 40,
          context.canvas.height - hudHeight + 85
        );
        damage =
          this.ownPlayer.weapon.item_type.Knife.damage +
          ClientGame.getModifierDamage(
            this.ownPlayer.weapon.item_type.Knife.modifiers
          );
        range = this.ownPlayer.weapon.item_type.Knife.range;
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'Boots')) {
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'Armor')) {
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'MedKit')) {
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'Grenade')) {
      } else if (enumIs(this.ownPlayer.weapon.item_type, 'EnergyDrink')) {
      } else {
        unreachable(this.ownPlayer.weapon.item_type);
      }
    } else {
      const textLines = ['Find a', 'weapon!'];

      for (let i = 0; i < textLines.length; i++) {
        const text = textLines[i];
        context.fillText(
          text,
          context.canvas.width / 2 - hudWidth / 2 + 40,
          context.canvas.height - hudHeight + 44 - 10 + i * 12
        );
      }
    }

    // draw dummy player move speed "Move speed: 200"
    context.fillStyle = 'white';
    context.font = '11px sans-serif';
    context.textAlign = 'left';
    context.textBaseline = 'middle';

    const stats = [
      ['Damage', damage.toFixed(0)],
      ['Attack Range', range],
      ['Attack Speed', this.ownPlayer.attackSpeed.toFixed(1)],
      ['Move speed', (this.ownPlayer.moveSpeed * 100).toFixed(0)],
      [
        'Damage reduction',
        (100 - (100 * 100) / (100 + this.ownPlayer.armor)).toFixed(0) + '%',
      ],
    ];

    for (let i = 0; i < stats.length; i++) {
      const [text, value] = stats[i];
      context.fillText(
        `${text}: ${value}`,
        context.canvas.width / 2 - hudWidth / 2 + 90,
        context.canvas.height - hudHeight + 20 + i * 15
      );
    }

    // draw inventory
    if (this.ownPlayer.inventory) {
      context.fillStyle = 'white';
      context.font = '10px sans-serif';
      context.textAlign = 'left';
      context.textBaseline = 'middle';
      context.strokeStyle = 'white';

      const queue = this.ownPlayer.queue.getQueue();
      for (let i = 0; i < this.ownPlayer.inventory.slots.length; i++) {
        const item = this.ownPlayer.inventory.slots[i][0];

        const slotInUse = queue.find(
          (a) =>
            (enumIs(a.action, 'UseItem') && a.action.UseItem.slot == i) ||
            (enumIs(a.action, 'UseItemAt') && a.action.UseItemAt.slot == i)
        );

        const slotX =
          context.canvas.width / 2 + hudWidth / 2 - 130 + (i % 3) * 40;
        const slotY =
          context.canvas.height - hudHeight + 10 + Math.floor(i / 3) * 40;
        const slotWidth = 40;
        const slotHeight = 40;

        if (slotInUse) {
          // draw over the original slot rect with green
          context.fillStyle = '#80FF0080';
          context.fillRect(slotX, slotY, slotWidth, slotHeight);
        } else if (ClientGame.itemHasModifier(item)) {
          context.fillStyle = '#0000FF80';
          context.fillRect(slotX, slotY, slotWidth, slotHeight);
        }

        context.strokeStyle = 'white';
        context.strokeRect(slotX, slotY, slotWidth, slotHeight);

        if (!item) {
          continue;
        }

        hudRegions.push({
          type: 'inventorySlot',
          item,
          slot: i,
          x: slotX,
          y: slotY,
          w: slotWidth,
          h: slotHeight,
        });
        let itemCount: number | null = null;
        let itemImage: HTMLImageElement | null = null;

        if (enumIs(item.item_type, 'Knife')) {
          itemImage = assets.images.items.knife;
        } else if (enumIs(item.item_type, 'Pistol')) {
          itemImage = assets.images.items.pistol;
        } else if (enumIs(item.item_type, 'Boots')) {
          itemImage = assets.images.items.boots;
        } else if (enumIs(item.item_type, 'MedKit')) {
          itemImage = assets.images.items.medkit2;
          itemCount = item.item_type.MedKit.count;
        } else if (enumIs(item.item_type, 'Grenade')) {
          itemImage = assets.images.items.grenade;
          itemCount = item.item_type.Grenade.count;
        } else if (enumIs(item.item_type, 'Armor')) {
          itemImage = assets.images.items.armor;
        } else if (enumIs(item.item_type, 'EnergyDrink')) {
          itemImage = assets.images.items.energydrink;
          itemCount = item.item_type.EnergyDrink.count;
        } else {
          unreachable(item.item_type);
        }

        context.drawImage(itemImage, slotX + 4, slotY + 4, 32, 32);
        context.fillStyle = 'white';

        context.fillRect(slotX + 30, slotY, 10, 10);

        context.fillStyle = 'black';
        context.fillText(`${i + 1}`, slotX + 30 + 2, slotY + 5);

        if (itemCount > 1) {
          context.fillStyle = 'white';

          context.fillRect(slotX + 26, slotY + 30, 15, 10);
          context.fillStyle = 'black';
          context.fillText(`x${itemCount}`, slotX + 26 + 2, slotY + 30 + 5);
        }
      }
    }

    // draw stance block
    const stanceWidth = 150;
    const stanceHeight = 100;
    const stancePadding = 10;
    const stanceColumnWidth = (stanceWidth - stancePadding * 3) / 2;

    hudRegions.push({
      type: 'stance',
      stance: { Defensive: null },
      x: context.canvas.width / 2 + hudWidth / 2 + 30 + stancePadding,
      y: context.canvas.height - stanceHeight + stancePadding,
      w: stanceColumnWidth,
      h: stanceHeight - stancePadding * 2,
    });

    hudRegions.push({
      type: 'stance',
      stance: { Offensive: null },
      x:
        context.canvas.width / 2 +
        hudWidth / 2 +
        30 +
        stancePadding +
        stanceColumnWidth +
        10,
      y: context.canvas.height - stanceHeight + stancePadding,
      w: stanceColumnWidth,
      h: stanceHeight - stancePadding * 2,
    });

    context.fillStyle = 'rgba(100, 116, 139, 0.8)';
    context.fillRect(
      context.canvas.width / 2 + hudWidth / 2 + 30,
      context.canvas.height - stanceHeight,
      stanceWidth,
      stanceHeight
    );

    if (this.ownPlayer.stance) {
      if (enumIs(this.ownPlayer.stance, 'Defensive')) {
        // draw 2px white rect around hud region
        context.strokeStyle = 'white';
        context.lineWidth = 2;
        context.strokeRect(
          context.canvas.width / 2 + hudWidth / 2 + 30 + stancePadding,
          context.canvas.height - stanceHeight + stancePadding,
          stanceColumnWidth,
          stanceHeight - stancePadding * 2
        );
      } else if (enumIs(this.ownPlayer.stance, 'Offensive')) {
        context.strokeStyle = 'white';
        context.lineWidth = 2;
        context.strokeRect(
          context.canvas.width / 2 +
            hudWidth / 2 +
            30 +
            stancePadding +
            stanceColumnWidth +
            10,
          context.canvas.height - stanceHeight + stancePadding,
          stanceColumnWidth,
          stanceHeight - stancePadding * 2
        );
      } else {
        unreachable(this.ownPlayer.stance);
      }
    }

    context.fillStyle = 'white';
    context.font = '11px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    for (const [index, line] of ['Defensive', 'Stance'].entries()) {
      context.fillText(
        line,
        context.canvas.width / 2 +
          hudWidth / 2 +
          30 +
          stancePadding +
          stanceColumnWidth / 2,
        context.canvas.height - 55 + 20 + index * 14,
        stanceColumnWidth
      );
    }

    // draw assets.images.defensive above text
    context.drawImage(
      assets.images.defensive,
      context.canvas.width / 2 +
        hudWidth / 2 +
        30 +
        stancePadding +
        stanceColumnWidth / 2 -
        16,
      context.canvas.height - stanceHeight + 20,
      32,
      32
    );

    if (!this.ownPlayer.weapon) {
      context.globalAlpha = 0.5;
    }

    for (const [index, line] of ['Offensive', 'Stance'].entries()) {
      context.fillText(
        line,
        context.canvas.width / 2 +
          hudWidth / 2 +
          30 +
          stancePadding +
          10 +
          stanceColumnWidth +
          stanceColumnWidth / 2,
        context.canvas.height - 55 + 20 + index * 14,
        stanceColumnWidth
      );
    }

    context.drawImage(
      assets.images.offensive,
      context.canvas.width / 2 +
        hudWidth / 2 +
        30 +
        stancePadding +
        stanceColumnWidth / 2 -
        16 +
        stanceColumnWidth +
        10,
      context.canvas.height - stanceHeight + 20 - 3,
      32,
      32
    );

    context.globalAlpha = 1;

    return hudRegions;
  }

  public drawItems(context: CanvasRenderingContext2D, renderer: Renderer) {
    for (const item of this.visibleItems) {
      const { x, y } = item.loc;
      const { x: canvasX, y: canvasY } = renderer.toCanvasCoords(x, y);

      const displacement = Math.sin(Date.now() / 100 + item.id) * 5;

      if (enumIs(item.item_type, 'MedKit')) {
        context.drawImage(
          assets.images.items.medkit2,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'Boots')) {
        context.drawImage(
          assets.images.items.boots,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'Pistol')) {
        context.drawImage(
          assets.images.items.pistol,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'Knife')) {
        context.drawImage(
          assets.images.items.knife,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'Grenade')) {
        context.drawImage(
          assets.images.items.grenade,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'Armor')) {
        context.drawImage(
          assets.images.items.armor,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else if (enumIs(item.item_type, 'EnergyDrink')) {
        context.drawImage(
          assets.images.items.energydrink,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2 - displacement,
          renderer.tileSize,
          renderer.tileSize
        );
      } else {
        unreachable(item.item_type);
      }
    }

    // draw temporary statuses
    for (const [index, status] of this.ownPlayer.temporaryStatuses.entries()) {
      // draw each status above the hud in a 32x32 rect

      if (enumIs(status, 'Energized')) {
        // draw 32x32 icon above the hud on a white rect

        context.fillStyle = 'white';
        context.fillRect(
          context.canvas.width / 2 - 16 + 36 * index,
          context.canvas.height - 100 - 32,
          32,
          32
        );

        context.drawImage(
          assets.images.items.energydrink,
          context.canvas.width / 2 - 16 + 36 * index,
          context.canvas.height - 100 - 32,
          32,
          32
        );
      }
    }
  }

  public drawGameObjects(
    context: CanvasRenderingContext2D,
    renderer: Renderer
  ) {
    for (const obj of this.gameObjects) {
      if (enumIs(obj.object_type, 'Grenade')) {
        if (!obj.object_type.Grenade.loc) {
          continue;
        }

        const { x, y } = obj.object_type.Grenade.loc;
        const { x: canvasX, y: canvasY } = renderer.toCanvasCoords(x, y);
        context.drawImage(
          assets.images.items.grenade,
          canvasX - renderer.tileSize / 2,
          canvasY - renderer.tileSize / 2,
          renderer.tileSize,
          renderer.tileSize
        );
      } else {
        unreachable(obj.object_type);
      }
    }
  }

  public drawMessages(context: CanvasRenderingContext2D) {
    const messages = this.messages.getMessages(Date.now());
    const padding = 16;
    context.fillStyle = 'white';
    context.font = '20px sans-serif';
    context.textAlign = 'left';

    // text stroke black
    context.strokeStyle = 'black';

    context.textBaseline = 'top';
    for (const [index, message] of messages.entries()) {
      context.strokeText(
        message.text,
        padding,
        70 + index * 20,
        context.canvas.width - padding * 2
      );
      context.fillText(
        message.text,
        padding,
        70 + index * 20,
        context.canvas.width - padding * 2
      );
    }
  }

  drawTabInfo(context: CanvasRenderingContext2D) {
    const padding = 20;
    const lineHeight = 30;
    const height =
      (this.playerPublicInfo.length + 1) * lineHeight + padding * 2;
    const width = 300;

    context.fillStyle = 'rgba(100, 116, 139, 0.6)';
    context.fillRect(
      context.canvas.width / 2 - width / 2,
      context.canvas.height / 2 - height / 2,
      width,
      height
    );

    context.fillStyle = 'white';
    context.font = '16px sans-serif';

    const players = [...this.playerPublicInfo].sort((a, b) => {
      if (a.dead && !b.dead) {
        return 1;
      } else if (!a.dead && b.dead) {
        return -1;
      }
      return b.kills - a.kills;
    });

    context.fillText(
      'Player',
      context.canvas.width / 2 - width / 2 + padding,
      context.canvas.height / 2 - height / 2 + padding,
      width - padding * 2
    );
    context.textAlign = 'right';

    context.fillText(
      'Kills',
      context.canvas.width / 2 + width / 2 - padding,
      context.canvas.height / 2 - height / 2 + padding,
      width - padding * 2
    );

    context.strokeStyle = 'white';
    // underline first row
    context.beginPath();
    context.moveTo(
      context.canvas.width / 2 - width / 2 + padding,
      context.canvas.height / 2 - height / 2 + padding + lineHeight - 10
    );
    context.lineTo(
      context.canvas.width / 2 + width / 2 - padding,
      context.canvas.height / 2 - height / 2 + padding + lineHeight - 10
    );
    context.stroke();

    for (const [index, player] of players.entries()) {
      if (player.dead) {
        context.fillStyle = 'rgba(255,255,255,0.5)';
      } else {
        context.fillStyle = 'white';
      }
      context.textAlign = 'left';

      context.fillText(
        player.dead ? player.name + ' (dead)' : player.name,
        context.canvas.width / 2 - width / 2 + padding,
        context.canvas.height / 2 -
          height / 2 +
          padding +
          (index + 1) * lineHeight,
        width - padding * 2
      );
      context.textAlign = 'right';

      context.fillText(
        player.kills.toString(),
        context.canvas.width / 2 + width / 2 - padding,
        context.canvas.height / 2 -
          height / 2 +
          padding +
          (index + 1) * lineHeight,
        width - padding * 2
      );
    }
  }
}
